/**
 * Consent, shown before any CV/answer is processed (constitution §V) —
 * `POST /api/onboarding/consent`.
 *
 * The link to the full policy is load-bearing, not decoration: the server
 * records consent against `PRIVACY_POLICY_VERSION`, and agreeing to a version
 * of a document nobody can open is not informed consent. It opens `/privacy` in
 * a new tab so answering "what am I agreeing to?" does not cost the candidate
 * their place in onboarding.
 *
 * `onDecline` is required. An accept-only consent screen is a dark pattern: the
 * candidate's only exits were the back button and closing the tab, neither of
 * which tells them what happens to the data they already gave.
 */

import { useState } from 'react'
import { Link } from 'react-router'
import { recordConsent } from '../api/onboarding'
import { useI18n } from '../i18n'
import './ConsentGate.css'

interface ConsentGateProps {
  onAccept: () => void
  onDecline: () => void
}

export function ConsentGate({ onAccept, onDecline }: ConsentGateProps) {
  const { t } = useI18n()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAccept = async () => {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await recordConsent()
      onAccept()
    } catch {
      setError(t('common.error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="stack">
      <h2>{t('onboarding.consentTitle')}</h2>
      <p>{t('onboarding.consentBody')}</p>
      <Link to="/privacy" target="_blank" rel="noreferrer" className="consent-gate-policy">
        {t('onboarding.consentPolicyLink')}
      </Link>
      {error && <p className="form-error">{error}</p>}
      <div className="consent-gate-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void handleAccept()}
          disabled={submitting}
        >
          {submitting ? t('common.loading') : t('onboarding.consentAccept')}
        </button>
        <button type="button" className="btn btn-outline" onClick={onDecline} disabled={submitting}>
          {t('onboarding.consentDecline')}
        </button>
      </div>
    </div>
  )
}
